import { MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, AccordionSection, Chip, InputCheckbox, Label, Span } from "@goodfill/ui-components";
import { calculateIssuersColor } from "./calculateIssuersColor";
import { IssuerWithPaymentRank } from "./getIssuersWithPaymentRank";
import { IssuerId } from "@goodfill/domain-properties";
import { toggleInArray } from "@goodfill/array";

export interface BondsIssuerSelectorProps {
  issuers: IssuerWithPaymentRank[];
  issuerSearchString: string;
  selectedIssuerIds: IssuerId[];
  isComparingCharts: boolean;
  onClickCompare: MouseEventHandler;
  setSelectedIssuerIds: (selectedIds: IssuerId[]) => void;
  onClickClear: MouseEventHandler;
  onClickCompareInRow: () => void;
  compareInRow: boolean;
}

export const BondsIssuerSelector: React.FC<BondsIssuerSelectorProps> = ({
  issuers,
  issuerSearchString,
  selectedIssuerIds,
  setSelectedIssuerIds,
  isComparingCharts,
  onClickCompare,
  onClickClear,
  onClickCompareInRow,
  compareInRow,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const toggleIssuer = useCallback(
    (id: IssuerId) => {
      setSelectedIssuerIds(toggleInArray(id, selectedIssuerIds));
    },
    [setSelectedIssuerIds, selectedIssuerIds],
  );

  const bondsIssuerList = useMemo(() => {
    if (!Array.isArray(issuers)) {
      return [];
    }

    return issuers.map((issuer) => {
      return {
        issuer,
        onChange: () => {
          toggleIssuer(issuer.id);
        },
        selected: selectedIssuerIds.includes(issuer.id),
        color: calculateIssuersColor(issuer.id),
      };
    });
  }, [issuers, toggleIssuer, selectedIssuerIds]);

  const noResultFound = useMemo(() => {
    return bondsIssuerList.length === 0 && issuerSearchString.length > 0;
  }, [bondsIssuerList, issuerSearchString]);

  useEffect(() => {
    if (issuerSearchString.length > 0) {
      setIsOpen(true);
    }
  }, [issuerSearchString]);

  return (
    <div className="flex flex-col gap-4 mt-1">
      <Accordion>
        <AccordionSection
          title="Select Bond"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          className="bg-white gray-primary"
          isBaseMargin={false}
          isBorder={true}
          isBaseHeight={false}
        >
          {noResultFound ? (
            <div className="flex flex-row justify-center my-4">
              <Span size="xl">No result</Span>
            </div>
          ) : null}
          <div className="flex justify-between mt-2">
            <div>
              <Chip
                size="small"
                primary={isComparingCharts}
                onClick={onClickCompare}
                className="px-0 py-5 blue-primary font-semibold"
              >
                Compare
              </Chip>
              {isComparingCharts ? (
                <>
                  {" "}
                  in{" "}
                  <Chip size="small" onClick={onClickCompareInRow} className="px-0 py-5 blue-primary font-semibold">
                    {compareInRow ? "Column" : "Row"}
                  </Chip>
                </>
              ) : null}
            </div>
            <Chip size="small" onClick={onClickClear} className="px-0 py-5 blue-primary font-semibold">
              Clear All
            </Chip>
          </div>
          <div className="max-h-112 custom-scrollbar overflow-x-hidden overflow-y-scroll">
            {bondsIssuerList.map(({ issuer, selected, onChange }, i) => (
              <div className="flex flex-row items-center gap-2 my-4 mx-1">
                <InputCheckbox id={String(issuer.id)} checked={selected} onChange={onChange} />
                <Label size="sm" uppercase={true} htmlFor={String(issuer.id)} accent={!selected} primary={selected}>
                  <div className="pl-2 flex items-center">
                    {issuer.paymentRank ? (
                      <>
                        {issuer.name}
                        <br />
                        {issuer.paymentRank}
                      </>
                    ) : (
                      <>{issuer.name}</>
                    )}
                  </div>
                </Label>
              </div>
            ))}
          </div>
        </AccordionSection>
      </Accordion>
    </div>
  );
};
